import React, { useCallback, useEffect, useMemo, useState } from "react";
import ShiftModalShell from "./ShiftModalShell";
import StatusTimeline from "../shiftAnalysis/StatusTimeline";
import { ChartDataService } from "../../services/ChartDataService";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import { Spinner } from "@digitalisation/enlixy-comp";
import { useThemeClasses } from "../../providers/ThemeProvider";
import type {
  StateInterval,
  StateCategory,
  FaultEvent,
  FaultRating,
  FaultSeverity,
} from "../../services/ShiftAnalysisService";

export interface ShiftStatusModalProps {
  machineSerialNo: string;
  /** Shift calendar day, "YYYY-MM-DD". */
  shiftDate: string;
  /** Shift name as stored in Doris, e.g. "B1". */
  shiftName: string;
  onClose: () => void;
}

/** Normalise a NATS response body into a flat array of rows. */
function asRows(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === "object") {
    const results = (data as { results?: unknown }).results;
    if (Array.isArray(results)) return results as Record<string, unknown>[];
  }
  return [];
}

const str = (v: unknown, fallback = ""): string =>
  v === null || v === undefined ? fallback : String(v);

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Format a second count as "1h 04m 12s" / "4m 12s" / "12s". */
function fmtDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

/**
 * Interval drill-down for one machine+shift: what the machine was actually
 * doing, minute by minute, behind a single stacked column.
 *
 * The state intervals come from `PC.machine_status_over_shift`, which mirrors
 * the bar chart's own statement (same overlap predicate and the same
 * GREATEST/LEAST clipping to the shift window), so the durations here sum back
 * to the column segments the user clicked. Unlike the production KPIs, this
 * works on BDE2000 machines too — `mv_machine_states` covers both releases.
 *
 * Rendering reuses Shift Analysis' `StatusTimeline` with a single-machine map,
 * so the lanes, synchronised crosshair and per-instant readout behave exactly
 * as they do on the line view.
 */
const ShiftStatusModal: React.FC<ShiftStatusModalProps> = ({
  machineSerialNo,
  shiftDate,
  shiftName,
  onClose,
}) => {
  const { token } = useUnifiedAuth();
  const themeClasses = useThemeClasses();
  const [states, setStates] = useState<StateInterval[]>([]);
  const [faults, setFaults] = useState<FaultEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { states: statesRes, faults: faultsRes } =
        await ChartDataService.fetchMachineStatusOverShift(
          {
            serialno: machineSerialNo,
            shift_date: shiftDate,
            shift_name: shiftName,
          },
          token,
        );

      if (!statesRes.success) {
        throw new Error(statesRes.error ?? "Request failed");
      }

      setStates(
        asRows(statesRes.data).map((r) => ({
          machine_sn: str(r.machine_sn, machineSerialNo),
          machine_name: str(r.machine_name, machineSerialNo),
          state_start: str(r.state_start),
          state_end: str(r.state_end),
          duration_s: num(r.duration_s),
          state_text: str(r.state_text),
          state_category: str(r.state_category, "standby") as StateCategory,
        })),
      );

      // Faults are optional context — a failed or empty response still leaves a
      // perfectly usable state timeline.
      setFaults(
        faultsRes?.success
          ? asRows(faultsRes.data).map((r) => ({
              machine_sn: str(r.machine_sn, machineSerialNo),
              machine_name: str(r.machine_sn, machineSerialNo),
              event_start: str(r.event_start),
              event_end: str(r.event_end),
              duration_s: num(r.duration_s),
              rating: str(r.rating, "undefined") as FaultRating,
              fault_text: str(r.fault_text),
              severity: str(r.severity, "minor") as FaultSeverity,
            }))
          : [],
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load shift states");
    } finally {
      setLoading(false);
    }
  }, [machineSerialNo, shiftDate, shiftName, token]);

  useEffect(() => {
    load();
  }, [load]);

  const stateLanes = useMemo(() => {
    const map = new Map<string, StateInterval[]>();
    if (states.length) map.set(machineSerialNo, states);
    return map;
  }, [states, machineSerialNo]);

  const faultLanes = useMemo(() => {
    const map = new Map<string, FaultEvent[]>();
    if (faults.length) map.set(machineSerialNo, faults);
    return map;
  }, [faults, machineSerialNo]);

  /** Running vs stopped split, straight from the interval durations. */
  const summary = useMemo(() => {
    let running = 0;
    let stoppedInt = 0;
    let stoppedExt = 0;
    for (const s of states) {
      if (s.state_category === "running") running += s.duration_s;
      else if (s.state_category === "waiting_ext") stoppedExt += s.duration_s;
      else stoppedInt += s.duration_s;
    }
    const total = running + stoppedInt + stoppedExt;
    return { running, stoppedInt, stoppedExt, total };
  }, [states]);

  const subtitle = (
    <>
      {shiftDate} • Shift {shiftName} • {machineSerialNo}
      {summary.total > 0 && (
        <>
          {" • "}
          <span className={themeClasses.textSecondary}>
            running <b>{fmtDuration(summary.running)}</b> (
            {((summary.running / summary.total) * 100).toFixed(1)}%) • stopped
            int <b>{fmtDuration(summary.stoppedInt)}</b> • ext{" "}
            <b>{fmtDuration(summary.stoppedExt)}</b>
          </span>
        </>
      )}
    </>
  );

  return (
    <ShiftModalShell
      title="Operating states over shift"
      subtitle={subtitle}
      onClose={onClose}
      widthClass="max-w-6xl"
    >
      <div className="px-4 py-4 max-h-[80vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="flex items-center space-x-2">
              <Spinner size={24} className="text-blue-600" />
              <span className={themeClasses.textSecondary}>
                Loading shift states…
              </span>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="text-red-500 font-medium">{error}</div>
            <button
              onClick={load}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
            >
              Retry
            </button>
          </div>
        ) : (
          <StatusTimeline
            stateLanes={stateLanes}
            machines={[]}
            faultLanes={faultLanes}
          />
        )}
      </div>
    </ShiftModalShell>
  );
};

export default ShiftStatusModal;
